import React, { useState, useCallback, useRef } from 'react'
import styles from './MediaView.module.scss'
import { useQuery, useLazyQuery, useMutation } from '@apollo/client'
import {
  LIST_DELIVERIES_QUERY,
  GET_DELIVERY_QUERY,
  CREATE_DELIVERY_MUTATION,
  INDEX_DELIVERY_MUTATION,
  SET_DELIVERY_VISIBILITY_MUTATION,
  DELETE_DELIVERY_MUTATION,
  GET_DELIVERY_DOWNLOAD_QUERY,
  CREATE_UPLOAD_GRANT_MUTATION,
  GET_PROJECTS_QUERY,
} from '../query/gqlQueries'
import { uploadFile, CHUNK_THRESHOLD } from '../uploadMedia'

import CloseIcon from '@mui/icons-material/Close'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AddIcon from '@mui/icons-material/Add'
import RefreshIcon from '@mui/icons-material/Refresh'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import DownloadIcon from '@mui/icons-material/Download'
import PublicIcon from '@mui/icons-material/Public'
import LockIcon from '@mui/icons-material/Lock'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import PlayCircleIcon from '@mui/icons-material/PlayCircle'

const IMAGE_STORE_URI = process.env.REACT_APP_IMAGE_STORE_URI
function storeUrl(url) {
  if (!url || !IMAGE_STORE_URI) return url
  try {
    const u = new URL(url)
    return IMAGE_STORE_URI.replace(/\/$/, '') + u.pathname + u.search
  } catch { return url }
}

function formatBytes(bytes) {
  if (!bytes || bytes < 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let i = 0, n = bytes
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++ }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 2)} ${units[i]}`
}

function prettyDate(d) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d || '')) return d || ''
  const [y, m, day] = d.split('-').map(Number)
  return new Date(y, m - 1, day).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

const todayISO = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// Finished files handed to a client. Separate from the Library because they
// are a different kind of thing: exports are fixed, dated, and client-facing,
// where originals are immutable and internal. Re-exporting makes a new
// delivery rather than mutating the old one — a client who downloaded
// something last month should still find exactly what they downloaded.
export default function MediaDeliveries({ username, onBack, setIsVisible }) {
  const [selectedId, setSelectedId] = useState(null)
  const [isCreating, setIsCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [newProject, setNewProject] = useState('')
  const [newDate, setNewDate] = useState(todayISO())
  const [newNotes, setNewNotes] = useState('')
  const [createError, setCreateError] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [uploadMsg, setUploadMsg] = useState(null)
  const [downloadHint, setDownloadHint] = useState(null)
  const uploadInputRef = useRef(null)

  const { data: projectsData } = useQuery(GET_PROJECTS_QUERY, { variables: { username }, skip: !username })
  const existingProjects = projectsData?.getProjects || []

  const { data: listData, loading: listLoading, refetch: refetchList } =
    useQuery(LIST_DELIVERIES_QUERY, { variables: { project: null }, skip: !username, fetchPolicy: 'cache-and-network' })
  const [fetchDetail, { data: detailData, refetch: refetchDetail }] =
    useLazyQuery(GET_DELIVERY_QUERY, { fetchPolicy: 'cache-and-network' })
  const [fetchDownload] = useLazyQuery(GET_DELIVERY_DOWNLOAD_QUERY, { fetchPolicy: 'network-only' })

  const [createDelivery, { loading: creating }] = useMutation(CREATE_DELIVERY_MUTATION)
  const [indexDelivery] = useMutation(INDEX_DELIVERY_MUTATION)
  const [setVisibility] = useMutation(SET_DELIVERY_VISIBILITY_MUTATION)
  const [deleteDelivery] = useMutation(DELETE_DELIVERY_MUTATION)
  const [createUploadGrant] = useMutation(CREATE_UPLOAD_GRANT_MUTATION)

  const deliveries = listData?.listDeliveries || []
  const detail = detailData?.getDelivery
  const delivery = detail?.delivery
  const files = detail?.files || []

  const grantRef = useRef(null)
  const getUploadUrl = useCallback(async (rel) => {
    const c = grantRef.current
    if (c && c.path === rel && c.expiresAtMs - Date.now() > 120000) return c.url
    const { data } = await createUploadGrant({ variables: { path: rel } })
    const g = data?.createUploadGrant
    if (!g?.url) throw new Error('Could not get permission to upload here')
    grantRef.current = { path: rel, url: storeUrl(g.url), expiresAtMs: new Date(g.expiresAt).getTime() }
    return grantRef.current.url
  }, [createUploadGrant])

  const open = useCallback((id) => {
    setSelectedId(id)
    setDownloadHint(null)
    fetchDetail({ variables: { identity: id } })
  }, [fetchDetail])

  const handleCreate = useCallback(async (e) => {
    e?.preventDefault?.()
    if (!newName.trim() || !newProject) return
    setCreateError(null)
    try {
      const { data } = await createDelivery({
        variables: {
          input: {
            name: newName.trim(), project: newProject,
            deliveredOn: newDate || null, notes: newNotes.trim() || null,
          },
        },
      })
      setIsCreating(false)
      setNewName(''); setNewProject(''); setNewNotes(''); setNewDate(todayISO())
      await refetchList()
      const id = data?.createDelivery?.identity
      if (id) open(id)
    } catch (err) {
      setCreateError(err.message || 'Could not create the delivery.')
    }
  }, [newName, newProject, newDate, newNotes, createDelivery, refetchList, open])

  // Exports go straight into the delivery folder — unlike originals, they are
  // not EXIF-sorted. An export's name is chosen for the client, and its
  // capture date belongs to the original it came from, not to the file.
  const handleUpload = useCallback(async (fileList) => {
    const list = Array.from(fileList || [])
    if (!list.length || !delivery) return
    const rel = delivery.properties.path
    setUploading(true)
    let ok = 0
    const failed = []
    for (let i = 0; i < list.length; i++) {
      const f = list[i]
      const label = `${i + 1}/${list.length} — ${f.name}`
      setUploadMsg(`Uploading ${label}…`)
      try {
        await uploadFile(f, () => getUploadUrl(rel), (frac) => {
          setUploadMsg(f.size > CHUNK_THRESHOLD && frac < 1
            ? `Uploading ${label} — ${Math.round(frac * 100)}%`
            : `Uploading ${label}…`)
        })
        ok++
      } catch (err) {
        failed.push(`${f.name} — ${err.message}`)
      }
    }
    if (uploadInputRef.current) uploadInputRef.current.value = ''
    setUploadMsg(`Uploaded ${ok}/${list.length} — matching to originals…`)
    try {
      await indexDelivery({ variables: { identity: selectedId } })
      await refetchDetail({ identity: selectedId })
      await refetchList()
      setUploadMsg(`${ok} added${failed.length ? ` · ${failed.length} failed` : ''}`)
    } catch (err) {
      setUploadMsg(`Uploaded ${ok}, but indexing failed: ${err.message}`)
    }
    setUploading(false)
    if (failed.length) window.alert(`${ok} uploaded, ${failed.length} failed:\n\n${failed.join('\n')}`)
  }, [delivery, selectedId, getUploadUrl, indexDelivery, refetchDetail, refetchList])

  const handleReindex = useCallback(async () => {
    if (!selectedId) return
    setUploadMsg('Rescanning the folder…')
    try {
      await indexDelivery({ variables: { identity: selectedId } })
      await refetchDetail({ identity: selectedId })
      await refetchList()
      setUploadMsg(null)
    } catch (err) {
      setUploadMsg(err.message)
    }
  }, [selectedId, indexDelivery, refetchDetail, refetchList])

  const handleToggleVisibility = useCallback(async () => {
    if (!delivery) return
    const next = delivery.properties.visibility === 'client' ? 'internal' : 'client'
    try {
      await setVisibility({ variables: { identity: selectedId, visibility: next } })
      await refetchDetail({ identity: selectedId })
      await refetchList()
    } catch (err) {
      window.alert(err.message || 'Could not change visibility')
    }
  }, [delivery, selectedId, setVisibility, refetchDetail, refetchList])

  const handleDownload = useCallback(async () => {
    if (!selectedId) return
    try {
      const { data } = await fetchDownload({ variables: { identity: selectedId } })
      const dl = data?.getDeliveryDownload
      if (!dl || !dl.files.length) { window.alert('Nothing in this delivery yet.'); return }
      const gb = (dl.totalBytes / 1073741824).toFixed(2)
      const lines = [
        `# ${dl.shoot} — ${dl.fileCount} files, ${gb} GB`,
        `# Links expire ${new Date(dl.expiresAt).toLocaleString()}.`,
        '#',
        '# In the folder you want these in, run:',
        '#     curl --parallel --parallel-max 4 -C - -K THIS_FILE',
        '',
      ]
      for (const f of dl.files) {
        lines.push(`url = "${storeUrl(f.url)}"`)
        lines.push(`output = "${f.output}"`)
        lines.push('')
      }
      const blob = new Blob([lines.join('\n')], { type: 'text/plain' })
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `${(dl.shoot || 'delivery').replace(/[^a-zA-Z0-9-_]+/g, '-').toLowerCase()}.curl`
      document.body.appendChild(a); a.click(); document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(a.href), 10000)
      setDownloadHint({ file: a.download, count: dl.fileCount, gb })
    } catch (err) {
      window.alert(err.message || 'Could not prepare the download')
    }
  }, [selectedId, fetchDownload])

  const handleDelete = useCallback(async () => {
    if (!window.confirm(
      'Delete this delivery record?\n\n' +
      'The exported files stay on the drive — a client may already have them, ' +
      'so removing the record never destroys the deliverable.'
    )) return
    try {
      await deleteDelivery({ variables: { identity: selectedId } })
      setSelectedId(null)
      await refetchList()
    } catch (err) {
      window.alert(err.message || 'Could not delete')
    }
  }, [selectedId, deleteDelivery, refetchList])

  // ───────────────────── DETAIL ─────────────────────
  if (selectedId && delivery) {
    const p = delivery.properties
    const isClient = p.visibility === 'client'
    return (
      <div className={styles.view}>
        <div className={styles.gridBg} />
        <button className={styles.closeBtn} onClick={() => setIsVisible(false)}><CloseIcon /></button>
        <div className={styles.container}>
          <header className={styles.header}>
            <button className={styles.backBtn} onClick={() => setSelectedId(null)}>
              <ArrowBackIcon fontSize="small" /> Deliveries
            </button>
            <div className={styles.titleGroup}>
              <div className={styles.label}>{p.project}</div>
              <h1 className={styles.title}>{p.name}</h1>
              {p.notes && <p className={styles.notes}>{p.notes}</p>}
            </div>
            <div className={styles.toolbar}>
              <input ref={uploadInputRef} type="file" multiple style={{ display: 'none' }}
                onChange={(e) => handleUpload(e.target.files)} />
              <button className={`${styles.iconBtn} ${styles.primaryBtn}`} disabled={uploading}
                onClick={() => uploadInputRef.current?.click()}>
                <FileUploadIcon fontSize="small" /> {uploading ? 'Uploading…' : 'Add exports'}
              </button>
              <button className={styles.iconBtn} disabled={!files.length} onClick={handleDownload}>
                <DownloadIcon fontSize="small" /> Download
              </button>
              <button className={styles.iconBtn} title="Rescan the folder on disk" onClick={handleReindex}>
                <RefreshIcon fontSize="small" />
              </button>
              <button className={styles.iconBtn}
                title={isClient ? 'Visible to clients granted this project. Click to make internal.' : 'Internal only. Click to share with the client.'}
                onClick={handleToggleVisibility}>
                {isClient ? <PublicIcon fontSize="small" /> : <LockIcon fontSize="small" />}
              </button>
              <button className={`${styles.iconBtn} ${styles.dangerBtn}`} onClick={handleDelete}>
                <DeleteOutlineIcon fontSize="small" />
              </button>
            </div>
          </header>

          <div className={styles.detailMeta}>
            <div className={styles.metaRow}>
              <span className={styles.metaLabel}>Delivered</span>
              <span className={styles.metaValue}>{prettyDate(p.deliveredOn) || '—'}</span>
            </div>
            <div className={styles.metaRow}>
              <span className={styles.metaLabel}>Contents</span>
              <span className={styles.metaValue}>
                {p.fileCount} file{p.fileCount === 1 ? '' : 's'} · {formatBytes(p.totalBytes)}
              </span>
            </div>
            <div className={styles.metaRow}>
              <span className={styles.metaLabel}>Visibility</span>
              <span className={styles.metaValue}>
                {isClient ? 'Shared — clients granted this project can download it' : 'Internal only'}
              </span>
            </div>
            <div className={styles.metaRow}>
              <span className={styles.metaLabel}>Folder</span>
              <span className={styles.metaValue} title={p.path}>{p.path}</span>
            </div>
          </div>

          {uploadMsg && <div className={styles.importMsg}>{uploadMsg}</div>}

          {downloadHint && (
            <div className={styles.downloadPanel}>
              <div className={styles.downloadHead}>
                Saved <code>{downloadHint.file}</code> — {downloadHint.count} files, {downloadHint.gb} GB
              </div>
              <pre className={styles.downloadCmd}>curl --parallel --parallel-max 4 -C - -K ~/Downloads/{downloadHint.file}</pre>
              <button className={styles.iconBtn} onClick={() => setDownloadHint(null)}>Dismiss</button>
            </div>
          )}

          {files.length === 0 && (
            <div className={styles.emptyState}>
              <p>No exports in this delivery yet.</p>
              <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
                Add them here, or drop them into <code>{p.path}/</code> over SMB and hit rescan.
                Each one is matched back to the original it came from by filename.
              </p>
            </div>
          )}

          <div className={styles.photoGrid}>
            {files.map((f) => (
              <div key={f.path} className={styles.photoCard}>
                <div className={styles.photoThumb}>
                  {f.thumbnailUrl ? (
                    <img src={storeUrl(f.thumbnailUrl)} alt={f.filename} loading="lazy"
                      onError={(e) => { e.currentTarget.style.opacity = 0.2 }} />
                  ) : (
                    <div className={styles.photoThumbPlaceholder}>
                      {f.kind === 'video' ? <PlayCircleIcon fontSize="small" /> : <InsertDriveFileIcon fontSize="small" />}
                      <span>{f.kind}</span>
                    </div>
                  )}
                </div>
                <div className={styles.photoMeta}>
                  <div className={styles.photoName} title={f.filename}>{f.filename}</div>
                  <div className={styles.photoStats}>
                    <span>{formatBytes(f.size)}</span>
                    <a className={styles.sidecarChip} href={storeUrl(f.downloadUrl)} download={f.filename}>⬇</a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  // ───────────────────── LIST ─────────────────────
  const byProject = deliveries.reduce((acc, d) => {
    const k = d.properties.project || 'Ungrouped'
    ;(acc[k] = acc[k] || []).push(d)
    return acc
  }, {})

  return (
    <div className={styles.view}>
      <div className={styles.gridBg} />
      <button className={styles.closeBtn} onClick={() => setIsVisible(false)}><CloseIcon /></button>
      <div className={styles.container}>
        <header className={styles.header}>
          <button className={styles.backBtn} onClick={onBack}>
            <ArrowBackIcon fontSize="small" /> Shoots
          </button>
          <div className={styles.titleGroup}>
            <div className={styles.label}>Media · finished work</div>
            <h1 className={styles.title}>Deliveries</h1>
          </div>
          <div className={styles.toolbar}>
            <button className={`${styles.iconBtn} ${styles.primaryBtn}`} onClick={() => setIsCreating(true)}>
              <AddIcon fontSize="small" /> New Delivery
            </button>
            <button className={styles.iconBtn} onClick={() => refetchList()}><RefreshIcon fontSize="small" /></button>
          </div>
        </header>

        {isCreating && (
          <form className={styles.createForm} onSubmit={handleCreate}>
            <div className={styles.createField}>
              <label className={styles.fieldLabel}>Project</label>
              <select className={styles.select} value={newProject}
                onChange={(e) => setNewProject(e.target.value)} autoFocus>
                <option value="">
                  {existingProjects.length ? 'Choose a project…' : 'No projects yet — create one in Work first'}
                </option>
                {existingProjects.map((pr) => (
                  <option key={pr.identity} value={pr.properties.title}>{pr.properties.title}</option>
                ))}
              </select>
            </div>
            <div className={styles.createField}>
              <label className={styles.fieldLabel}>Delivery name</label>
              <input type="text" className={styles.input} value={newName}
                onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Cork Range v2" />
            </div>
            <div className={styles.createField}>
              <label className={styles.fieldLabel}>Delivered on</label>
              <input type="date" className={styles.input} value={newDate}
                onChange={(e) => setNewDate(e.target.value)} />
            </div>
            <div className={styles.createField}>
              <label className={styles.fieldLabel}>Notes (optional)</label>
              <textarea className={styles.input} rows={2} value={newNotes}
                onChange={(e) => setNewNotes(e.target.value)}
                placeholder="What's in it, what changed since the last version" />
            </div>
            {createError && <div className={styles.errorMsg}>{createError}</div>}
            <div className={styles.createActions}>
              <button type="button" className={styles.iconBtn}
                onClick={() => { setIsCreating(false); setCreateError(null) }}>Cancel</button>
              <button type="submit" className={`${styles.iconBtn} ${styles.primaryBtn}`}
                disabled={creating || !newName.trim() || !newProject}>
                {creating ? 'Creating…' : 'Create delivery'}
              </button>
            </div>
          </form>
        )}

        {listLoading && !deliveries.length && <div className={styles.status}>Loading…</div>}
        {!listLoading && !deliveries.length && !isCreating && (
          <div className={styles.emptyState}>
            <p>No deliveries yet.</p>
            <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
              A delivery is a fixed, dated set of exports for one client. Re-exporting later
              makes a new version rather than changing this one.
            </p>
            <button className={`${styles.iconBtn} ${styles.primaryBtn}`} onClick={() => setIsCreating(true)}>
              <AddIcon fontSize="small" /> Create the first delivery
            </button>
          </div>
        )}

        {Object.keys(byProject).sort().map((proj) => (
          <div key={proj} className={styles.projectGroup}>
            <div className={styles.projectGroupHeader}>{proj}</div>
            <div className={styles.projectGrid}>
              {byProject[proj].map((d) => {
                const p = d.properties
                return (
                  <div key={d.identity} className={styles.projectCard} onClick={() => open(d.identity)}>
                    <div className={styles.projectCardHeader}>
                      <div className={styles.projectName}>{p.name}</div>
                      {p.visibility === 'client' && (
                        <span className={styles.privateBadge} title="Clients granted this project can download it">
                          <PublicIcon style={{ fontSize: 12 }} /> shared
                        </span>
                      )}
                    </div>
                    {p.notes && <div className={styles.projectNotes}>{p.notes}</div>}
                    <div className={styles.projectMeta}>
                      <span className={styles.slugTag}>{p.fileCount} file{p.fileCount === 1 ? '' : 's'} · {formatBytes(p.totalBytes)}</span>
                      <span>{prettyDate(p.deliveredOn)}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
